import { CurrencyData, OfferData } from '@wwe-portal/entities/offer.type';
import {
  OfferCardProps,
  CurrencyIcon,
  CurrencyIconProps,
  LayoutTypeEnum,
  OfferRewardItem,
  OfferTypeEnum,
} from '@wwe-portal/ui/features/offer';

import { NOT_READY } from '@wwe-portal/web/utils/constants/offer';
import { getEndDate, isDripOffer, isRubyOffer, isWebcoinOffer } from './utils';
import { RubiesIcon, WebCoinIcon } from '@wwe-portal/ui/components/icons';
import { requiresWebCoin } from '@wwe-portal/data/offers/offerUtils';
import { CurrencyType } from '@wwe-portal/entities/currency.type';
import { JSX } from 'react';
import { RegionalPriceType } from '@wwe-portal/entities/regional-price.type';

export const offerToItemProps = (offerData: Partial<OfferData>) => {
  const offerItemImage = offerData?.OfferItemImage;

  if (!Array.isArray(offerItemImage)) {
    return [];
  }

  return offerItemImage.map((image, index) => ({
    imageUrl: image,
    id: index,
    itemId: index.toString(),
    text: offerData?.OfferItemTexts?.[index]?.trim() || '',
    description: offerData?.OfferItemContent?.[index] ?? '',
    defaultImageUrl: offerData?.OfferItemImage?.[index],
    dynamicImageUrl: offerData?.OfferItemImageResize?.[index],
    offerType: offerData?.OfferItemTypes?.[index],
  }));
};

function formatPrice(price: number | undefined) {
  if (price == null || isNaN(price)) return null;
  return Number.isInteger(price) ? price : price.toFixed(2);
}

function getPriceComponent(
  priceNum: number | undefined,
  currentIconProps: CurrencyIconProps
) {
  if (priceNum === undefined) return undefined;
  if (priceNum === 0) return <>Claim</>;

  if (currentIconProps.offerType === OfferTypeEnum.AlternativeCurrency) {
    return (
      <div className="flex items-center justify-center">
        <CurrencyIcon {...currentIconProps} />
        <span className="mb-0 p-[5px] text-xs font-semibold text-white">
          {formatPrice(priceNum)}
        </span>
      </div>
    );
  } else {
    return (
      <>
        {formatPrice(priceNum)}
        {currentIconProps.offerType === OfferTypeEnum.Standard && ' USD'}
      </>
    );
  }
}

function getRegionalPriceAmount(regionalPrice: RegionalPriceType) {
  return regionalPrice.formatted.amount || '';
}

function getRegionalStrikethroughPriceAmount(regionalPrice: RegionalPriceType) {
  if (
    regionalPrice.formatted.amountNoDiscount !==
    getRegionalPriceAmount(regionalPrice)
  ) {
    return regionalPrice.formatted.amountNoDiscount;
  } else {
    return undefined;
  }
}

export type OfferPrice = {
  currentIconProps: {
    offerType: never;
    alternativeCostID: never;
  };
  price: JSX.Element | undefined;
  priceNum: number | undefined;
};

export function parseRegionalOfferPrice(
  offer?: Partial<OfferData>
): OfferPrice {
  const currentIconProps = {
    offerType: offer?.OfferType as never,
    alternativeCostID: offer?.AlternativeCostID as never,
  };
  if (offer && requiresWebCoin(offer)) {
    const amount = offer.AlternativeCostAmount;
    return {
      currentIconProps,
      price: getPriceComponent(amount, currentIconProps),
      priceNum: amount,
    };
  } else if (offer && offer.regionalPrice) {
    return {
      currentIconProps,
      price: <>{getRegionalPriceAmount(offer.regionalPrice)}</>,
      priceNum: Number.parseFloat(offer.regionalPrice.raw.amount),
    };
  } else {
    const amount = offer?.Price?.USD;
    return {
      currentIconProps,
      price: getPriceComponent(amount, currentIconProps),
      priceNum: amount,
    };
  }
}

export function parseRegionalOfferStrikethroughPrice(
  offer?: Partial<OfferData>
): OfferPrice {
  const currentIconProps = {
    offerType: offer?.OfferType as never,
    alternativeCostID: offer?.AlternativeCostID as never,
  };
  if (offer && requiresWebCoin(offer)) {
    return {
      currentIconProps,
      price: undefined,
      priceNum: undefined,
    };
  } else if (offer && offer.regionalPrice) {
    return {
      currentIconProps,
      price: <>{getRegionalStrikethroughPriceAmount(offer.regionalPrice)}</>,
      priceNum: Number.parseFloat(offer.regionalPrice.raw.amountNoDiscount),
    };
  } else {
    const amount = offer?.StrikeThroughPrice;
    return {
      currentIconProps,
      price: amount ? getPriceComponent(amount, currentIconProps) : undefined,
      priceNum: amount,
    };
  }
}

type CardStatusType = {
  isComingSoon: boolean;
  isNotReady: boolean;
  quantity?: number;
};

export const getCardStatus = (
  offerData: Partial<OfferData>,
  stockMap?: Map<string, number>
): CardStatusType => {
  const id = offerData?.OffersID ?? '';
  return {
    isComingSoon:
      !offerData.PortalAvailability && (offerData?.isDailyOffer || false),
    isNotReady:
      offerData.Availability === NOT_READY && !offerData?.isDailyOffer, // Daily offers can NOT be "Not Ready"
    quantity: stockMap?.get(id) ?? offerData?.StockQuantity,
  };
};

export const cardStatusLabel = ({
  isComingSoon,
  isNotReady,
}: {
  isComingSoon: boolean;
  isNotReady: boolean;
}) => {
  if (isComingSoon) {
    return 'COMING SOON';
  }
  if (isNotReady) {
    return 'NOT READY';
  }

  return null;
};

export const getStockQuantity = (
  offerData: Partial<OfferData>,
  options: {
    stockMap?: Map<string, number>;
    isAuthenticated: boolean;
  }
) => {
  if (!offerData.OffersID) return undefined;

  // If the user is not authenticated, we return the stock quantity
  if (!options.isAuthenticated) {
    return offerData?.StockQuantity;
  }

  // If the user is authenticated, we return the stock quantity from the stock map,
  // or 0 if the stock map is not available
  return options.stockMap?.get(offerData.OffersID) ?? 0;
};

export const offerToProps = (
  offer: Partial<OfferData>,
  options: {
    stockMap?: Map<string, number>;
    extendFn?: (offer: OfferCardProps) => OfferCardProps;
    currencies?: CurrencyData[];
    isAuthenticated: boolean;
  }
) => {
  const { stockMap, currencies, extendFn, isAuthenticated = false } = options;
  if (typeof offer === 'string') {
    offer = JSON.parse(offer);
  }

  const id = offer?.OffersID ?? '';

  const items = offerToItemProps(offer);
  const { isComingSoon, isNotReady } = getCardStatus(offer);

  const statusLabel = cardStatusLabel({
    isComingSoon,
    isNotReady,
  });

  const quantity = getStockQuantity(offer, { stockMap, isAuthenticated });
  const rewards = getRewardItems(offer, currencies);
  const dripOffer = isDripOffer(offer);
  const props: OfferCardProps = {
    id,
    backgroundImage: offer?.OfferBackgroundImage,
    dynamicBackgroundImage: offer?.OfferBackgroundImageResize,
    title: offer?.StoreItemName,
    strikethroughPrice: parseRegionalOfferStrikethroughPrice(offer).price,
    maxQuantity: offer?.StockQuantity,
    quantity,
    type: offer?.CombinationType as never,
    items,
    endDate: dripOffer
      ? offer?.EndDate
        ? new Date(offer.EndDate)
        : undefined
      : getEndDate(offer?.EndDate),
    hasPip: false,
    hasContest: offer.badgeType === 'multiple',
    badgeIcon: offer.badgeType === 'single' ? offer.badgeIcon : undefined,
    showStock: offer.ShowStock,
    price: statusLabel ?? parseRegionalOfferPrice(offer).price,
    currentIconProps: undefined,
    priceNum: undefined,
    soldOut: quantity !== undefined && quantity <= 0,
    totalItems: (offer?.offerItems?.length ?? 0) + rewards.length + 1,
    layoutType: offer.LayoutType as LayoutTypeEnum,
    storeItemDescription: offer.StoreItemDescription,
    offerDescriptionText: offer.OfferDescriptionText,
    bonusPercent: offer.BonusPercentage,
    badgeText: offer.badgeText,
    isDripOffer: dripOffer,
    dripDays: offer.dc?.b0,
  };

  if (extendFn) {
    return extendFn(props);
  }

  return props;
};

const getDescriptionItem = (
  quantity: string | number | undefined,
  name: string | undefined
) => {
  if (!quantity) return name;

  return `${quantity} ${name}`;
};

export const getRewardItems = (
  offerData: Partial<OfferData>,
  currencies: CurrencyData[] | undefined
) => {
  const usdPrice = offerData.Price?.USD;
  if (!usdPrice || !currencies) return [];
  const rewardItems: OfferRewardItem[] = [];
  const webcoinData = currencies.find(
    (item) => item.name === CurrencyType.WebCoin
  );
  const rubyData = currencies.find((item) => item.name === CurrencyType.Ruby);
  if (!isWebcoinOffer(offerData) && webcoinData) {
    rewardItems.push({
      itemId: CurrencyType.WebCoin,
      quantity: Math.round(webcoinData.rate * usdPrice),
      text: CurrencyType.WebCoin,
      description: getDescriptionItem(
        Math.round(webcoinData.rate * usdPrice),
        CurrencyType.WebCoin
      ),
      iconElement: (
        <WebCoinIcon
          className="size-full"
          style={{
            color: 'deepskyblue',
          }}
        />
      ),
    });
  }

  if (!isRubyOffer(offerData) && rubyData) {
    rewardItems.push({
      itemId: CurrencyType.Ruby,
      quantity: Math.round(rubyData.rate * usdPrice),
      text: CurrencyType.Ruby,
      description: getDescriptionItem(
        Math.round(rubyData.rate * usdPrice),
        CurrencyType.Ruby
      ),
      iconElement: (
        <RubiesIcon
          className="size-full"
          style={{
            color: '#c055ff',
          }}
        />
      ),
    });
  }

  return rewardItems;
};
